import { Info as InfoIcon } from '@mui/icons-material';
import { Alert, Box, Typography } from '@mui/material';
import type React from 'react';
import { useThemeMode } from '../contexts/ThemeContext';
import { useThemeColors } from '../hooks/useThemeColors';
import { IMG } from '../theme/assets';
import AppDialog from './AppDialog';

/** 手順。資料でも動画でも同じ手順なので、文言はここ1か所に置く */
const STEPS = [
  { t: '引用文をコピー', d: 'カードの「引用」の文をコピーします。' },
  {
    t: '資料／文字起こし内で検索を開く',
    d: '⌘ + F（Windows は Ctrl + F）で検索バーを開きます。',
  },
  {
    t: '貼り付けて検索',
    d: '該当箇所にジャンプします。タイムコードからも頭出しできます。',
  },
];

interface Props {
  open: boolean;
  onClose: () => void;
  /** 検証がまだ終わっていない画面で出す断り書き */
  notice?: string;
}

/**
 * 「引用の確認方法」モーダル。
 *
 * 同じ名前のボタンが結果画面と実行中画面の両方にあるのに、開く窓の中身が違っていた
 * （実行中の画面は見出しとひとことだけで、手順が書かれていなかった）ので1つにまとめた。
 * 読むだけの窓なので、閉じるのは見出しの × だけにしてある。
 */
const CitationHelpDialog: React.FC<Props> = ({ open, onClose, notice }) => {
  const tokens = useThemeColors();
  const { themeMode } = useThemeMode();
  const dark = themeMode === 'dark';

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      icon={<Box component="img" src={IMG.compliance.preview} alt="" />}
      title="引用の確認方法"
      subtitle="資料・動画内で引用された箇所を素早く見つける手順です。"
      size="md"
    >
      {notice && (
        <Alert severity="info" sx={{ mb: 2, fontSize: 12.5 }}>
          {notice}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
        {STEPS.map((s, i) => (
          <Box key={s.t} sx={{ display: 'flex', gap: 1.5 }}>
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                flexShrink: 0,
                bgcolor: tokens.teal,
                color: '#fff',
                fontWeight: 900,
                fontSize: 12,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              {i + 1}
            </Box>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: tokens.ink }}>
                {s.t}
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: tokens.muted, mt: 0.25, lineHeight: 1.5 }}>
                {s.d}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 1,
          mt: 2,
          p: 1.5,
          borderRadius: '11px',
          background: dark ? 'rgba(102,126,234,0.12)' : '#eef5ff',
          border: `1px solid ${dark ? 'rgba(102,126,234,0.3)' : '#d9e2ff'}`,
        }}
      >
        <InfoIcon sx={{ fontSize: 16, color: tokens.indigo, flexShrink: 0, mt: '1px' }} />
        <Typography
          sx={{ fontSize: 11.5, color: dark ? tokens.muted : '#3f4a6b', lineHeight: 1.55 }}
        >
          検索のコツ: ヒットしない時は短い特徴的な語句で検索すると見つかりやすくなります。
        </Typography>
      </Box>
    </AppDialog>
  );
};

export default CitationHelpDialog;
